import { FocusTrap } from 'focus-trap-react';
import * as React from 'react';
import { CSSTransition } from 'react-transition-group';

import { Portal } from '@xing-com/portal';

import { PopUpWindowPrimitive } from './pop-up-window.primitive';
import * as Styled from './pop-up-window.styles';
import type { PopUpWindowProps } from './pop-up-window.types';

const {
  dimmerDuration,
  dimmerTransitionName,
  popUpWindowDuration,
  popUpWindowTransitionName,
} = Styled;

export const PopUpWindow = React.forwardRef<HTMLDivElement, PopUpWindowProps>(
  (
    {
      children,
      className,
      closeButtonProps,
      focusTrapProps,
      hide,
      isFocusEnabled, // TODO: Remove as soon as the XMS team has migrated to the new focusTrapProps
      isShown,
      noPadding,
      onDimmerClick,
      ...props
    },
    forwardedRef
  ): JSX.Element => {
    const popUpWindowRef = React.useRef<HTMLDivElement>(null);
    const resolvedRef = forwardedRef || popUpWindowRef;

    // Focus management
    React.useEffect(() => {
      if (isShown) {
        // Get the actual DOM node
        const popUpElement =
          resolvedRef instanceof Function ? null : resolvedRef.current;

        // Set focus after a small delay to ensure rendering is complete
        setTimeout(() => {
          if (popUpElement) {
            popUpElement.focus();
          }
        }, 50);
      }
    }, [isShown, resolvedRef]);

    // Prevent scrolling when popup is open
    React.useEffect(() => {
      if (isShown) {
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
          document.body.style.overflow = originalOverflow;
        };
      }

      return undefined;
    }, [isShown]);

    // Keyboard handling
    React.useEffect(() => {
      const handleKey = (event: KeyboardEvent): void => {
        if (isShown && hide && event.key === 'Escape') {
          event.preventDefault();
          hide();
        }
      };

      if (isShown) {
        document.addEventListener('keydown', handleKey);
      }

      return () => {
        document.removeEventListener('keydown', handleKey);
      };
    }, [hide, isShown]);

    const { focusTrapOptions: userFocusTrapOptions, ...otherFocusTrapProps } =
      focusTrapProps || {};
    const mergedFocusTrapOptions = {
      allowOutsideClick: true,
      clickOutsideDeactivates: true,
      escapeDeactivates: Boolean(hide),
      fallbackFocus: () => {
        // If there is no focusable element, focus the container itself
        const container =
          resolvedRef instanceof Function ? null : resolvedRef.current;
        if (container) return container;
        return document.body;
      },
      returnFocusOnDeactivate: true,
      ...(userFocusTrapOptions || {}),
    };

    const PopUpWindowTransition = (
      <Styled.Wrapper
        aria-modal="true"
        className={className}
        role="dialog"
        tabIndex={-1}
        {...props}
      >
        <CSSTransition
          appear={isShown}
          classNames={popUpWindowTransitionName}
          in={isShown}
          key="popUpWindow"
          mountOnEnter
          timeout={parseInt(popUpWindowDuration)}
          unmountOnExit
          nodeRef={resolvedRef}
        >
          <FocusTrap
            active={isFocusEnabled ?? isShown}
            {...otherFocusTrapProps}
            focusTrapOptions={mergedFocusTrapOptions}
          >
            <PopUpWindowPrimitive
              closeButtonProps={closeButtonProps}
              hide={hide}
              noPadding={noPadding}
              ref={resolvedRef}
            >
              {children}
            </PopUpWindowPrimitive>
          </FocusTrap>
        </CSSTransition>
      </Styled.Wrapper>
    );

    const DimmerTransition = (
      <CSSTransition
        appear={isShown}
        classNames={dimmerTransitionName}
        in={isShown}
        key="dimmer"
        mountOnEnter
        timeout={parseInt(dimmerDuration)}
        unmountOnExit
      >
        <Styled.Dimmer
          data-qa="pop-up-window-dimmer"
          data-testid="pop-up-window-dimmer"
          onClick={onDimmerClick}
        />
      </CSSTransition>
    );

    return (
      <React.Fragment>
        <Portal>{PopUpWindowTransition}</Portal>
        {DimmerTransition}
      </React.Fragment>
    );
  }
);

PopUpWindow.displayName = 'PopUpWindow';
